/**
 * Manual-apply column mapping dialog (REQ-7.2), in two modes.
 *
 * "resolve" (the default): shown ONLY when at least one template column has
 * no title+type match on the board. For each unmatched column the user
 * chooses: map to an existing same-type column, create it, or skip its values.
 *
 * "all": opened on purpose from the Map columns button, before applying.
 * Every template column is listed with a Board column dropdown: the board's
 * same-type columns, then "+ Create", then "Don't fill". Columns that match
 * by title and type are pre-filled and marked "Same name"; a board column
 * picked in one row is greyed out in the others. The choices are for this
 * apply only, like the resolve mode's: nothing is saved to the template.
 *
 * Create is listed FIRST and preselected: it is the only action that fixes the
 * mismatch for good, by putting the column on the board. Map and Skip resolve
 * THIS apply only — the template is never edited behind the user's back — so both
 * surface a red callout saying the dialog will return until the template and the
 * board match. Create carries no callout.
 *
 * Automations never see this dialog; they skip unmatched columns and notify
 * (REQ-7.4).
 */
import { Dropdown, Modal, ModalBasicLayout, ModalContent, ModalFooter, ModalHeader, RadioButton } from '@vibe/core';
import { Warning } from '@vibe/icons';
import React, { useMemo, useState } from 'react';
import { typesMatch } from '../lib/columnMatching';
import './ApplyMappingDialog.css';

export default function ApplyMappingDialog({
  show,
  mode = 'resolve', // 'resolve' | 'all' (see above)
  unmatched, // template columns with no board match (single-board form)
  columns, // every template column (mode 'all', single-board form)
  idMap, // template column id → matched board column id (mode 'all')
  boardColumns,
  boardName, // named in the 'all' mode's lead
  usedBoardIds, // board column ids already taken by matched columns
  // The group apply matches TWO boards: `sections` replaces the props above
  // with one entry per board, [{ key, title, unmatched, columns, idMap,
  // boardColumns, usedBoardIds }], and every decision carries its section's key.
  sections,
  onConfirm, // (decisions: [{ section, column, action, targetId? }]) => void
  onCancel,
}) {
  const sectionList = useMemo(
    () => sections || [{ key: 'default', title: null, unmatched: unmatched || [], columns: columns || [], idMap: idMap || {}, boardColumns, usedBoardIds }],
    [sections, unmatched, columns, idMap, boardColumns, usedBoardIds],
  );
  const candidatesFor = useMemo(() => {
    const bySection = {};
    for (const s of sectionList) {
      // in 'all' mode every same-type column is a candidate; the rows keep each other apart below
      const used = new Set(mode === 'all' ? [] : [...(s.usedBoardIds || [])].map(String));
      bySection[s.key] = (col) =>
        (s.boardColumns || []).filter((bc) => !used.has(String(bc.id)) && typesMatch(col.type, bc.type));
    }
    return (col, sectionKey) => (bySection[sectionKey] || (() => []))(col);
  }, [sectionList, mode]);

  /**
   * Create is the default (and the first option) because it is the only action
   * that FIXES the mismatch: it puts the column on the board, so every later
   * apply — manual or automation — matches without asking. Map and Skip resolve
   * one run and leave the drift in place. targetId is still prefilled with the
   * best candidate so choosing Map is a single click.
   */
  const [decisions, setDecisions] = useState(() =>
    sectionList.flatMap((s) => {
      if (mode === 'all') {
        // every column: a match keeps its board column ("Same name"); the rest are created, as in the
        // resolve mode — never a guessed column, which could be one another row already fills
        return (s.columns || []).map((col) => {
          const matchedId = s.idMap ? s.idMap[String(col.id)] : undefined;
          if (matchedId !== undefined) return { section: s.key, column: col, action: 'map', targetId: matchedId, matchedId };
          const candidates = candidatesFor(col, s.key);
          return { section: s.key, column: col, action: 'create', targetId: candidates.length ? candidates[0].id : undefined };
        });
      }
      return (s.unmatched || []).map((col) => {
        const candidates = candidatesFor(col, s.key);
        return {
          section: s.key,
          column: col,
          action: 'create',
          targetId: candidates.length ? candidates[0].id : undefined,
        };
      });
    }),
  );
  const total = decisions.length;

  const setDecision = (index, patch) =>
    setDecisions((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  if (mode === 'all') {
    return (
      <Modal id="spt-map-columns" show={show} onClose={onCancel} size="medium">
        <ModalBasicLayout>
          <ModalHeader title="Map columns" />
          <ModalContent>
            <p className="mb-4 text-sm text-secondary">
              Choose which column{boardName ? <> on <strong className="text-primary">{boardName}</strong></> : ' on this board'} each template column fills.
            </p>
            <div className="spt-map" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {sectionList.map((s) => (
                <React.Fragment key={s.key}>
                  {s.title && <div className="spt-map__section">{s.title}</div>}
                  <div className="spt-map__head">
                    <span>Template column</span>
                    <span />
                    <span>Board column</span>
                    <span />
                  </div>
                  {decisions.map((d, i) => {
                    if (d.section !== s.key) return null;
                    const candidates = candidatesFor(d.column, d.section);
                    // a board column another row already fills is offered greyed out
                    const taken = new Set(decisions.filter((o, j) => j !== i && o.section === d.section && o.action === 'map' && o.targetId !== undefined).map((o) => String(o.targetId)));
                    const options = [
                      ...candidates.map((c) => ({ value: `map:${c.id}`, label: c.title, type: c.type, disabled: taken.has(String(c.id)) })),
                      { value: 'create', label: `+ Create “${d.column.title}”` },
                      { value: 'skip', label: "Don't fill" },
                    ];
                    const current = d.action === 'map' ? `map:${d.targetId}` : d.action;
                    const sameName = d.matchedId !== undefined && d.action === 'map' && String(d.targetId) === String(d.matchedId);
                    return (
                      <div key={`${d.section}-${d.column.id}`} className="spt-map__row">
                        <span className="spt-map__col">
                          <span className="font-semibold text-primary">{d.column.title}</span>
                          <span className="text-xs text-secondary font-mono">{d.column.type}</span>
                        </span>
                        <span className="spt-map__arrow" aria-hidden="true">
                          →
                        </span>
                        <Dropdown
                          size="small"
                          clearable={false}
                          aria-label={`Board column for ${d.column.title}`}
                          options={options}
                          value={options.find((o) => o.value === current) || options[options.length - 2]}
                          optionRenderer={(o) => (
                            <span className="spt-map__option">
                              <span>{o.label}</span>
                              {o.type && <span className="text-xs text-secondary font-mono">{o.type}</span>}
                            </span>
                          )}
                          onChange={(o) => {
                            if (!o) return;
                            if (o.value === 'create' || o.value === 'skip') setDecision(i, { action: o.value });
                            else setDecision(i, { action: 'map', targetId: candidates.find((c) => `map:${c.id}` === o.value).id });
                          }}
                        />
                        <span className="spt-map__same">{sameName ? '✓ Same name' : ''}</span>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </ModalContent>
        </ModalBasicLayout>
        <ModalFooter
          primaryButton={{ text: 'Apply template', onClick: () => onConfirm(decisions) }}
          secondaryButton={{ text: 'Cancel', onClick: onCancel }}
        />
      </Modal>
    );
  }

  return (
    <Modal id="spt-apply-mapping" show={show} onClose={onCancel} size="medium">
      <ModalBasicLayout>
        <ModalHeader title="Resolve columns to apply" />
        <ModalContent>
          <p className="mb-4 text-sm text-secondary">
            {total} column{total === 1 ? " doesn't" : "s don't"} match{' '}
            {sections ? 'the boards this template applies to' : "this board's subitems"}.
          </p>
          <div className="space-y-4" style={{ maxHeight: '46vh', overflowY: 'auto' }}>
            {sectionList.map((s) => (
              <React.Fragment key={s.key}>
                {s.title && decisions.some((d) => d.section === s.key) && (
                  <div className="text-sm font-semibold text-primary">{s.title}</div>
                )}
            {decisions.map((d, i) => {
              if (d.section !== s.key) return null;
              const candidates = candidatesFor(d.column, d.section);
              return (
                <div key={`${d.section}-${d.column.id}`} className="p-4 border border-subtle rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-semibold text-primary">{d.column.title}</span>
                    <span className="text-xs text-secondary font-mono">{d.column.type}</span>
                    <span className="ml-auto px-2 py-0.5 text-xs font-medium text-danger-ink bg-danger-tint rounded">
                      Not found on this board
                    </span>
                  </div>
                  {/* Order matters: the durable fix first, the one-run choices after. */}
                  <div className="space-y-2">
                    <RadioButton
                      text={`Create a new column "${d.column.title}"`}
                      name={`spt-map-${d.section}-${d.column.id}`}
                      checked={d.action === 'create'}
                      onSelect={() => setDecision(i, { action: 'create' })}
                    />
                    <div className="flex items-start gap-2">
                      <RadioButton
                        text="Map to an existing column"
                        name={`spt-map-${d.section}-${d.column.id}`}
                        checked={d.action === 'map'}
                        disabled={!candidates.length}
                        onSelect={() =>
                          setDecision(i, { action: 'map', targetId: d.targetId ?? (candidates[0] && candidates[0].id) })
                        }
                      />
                    </div>
                    {d.action === 'map' && candidates.length > 0 && (
                      <div className="ml-6" style={{ maxWidth: 280 }}>
                        <Dropdown
                          size="small"
                          clearable={false}
                          options={candidates.map((c) => ({ label: `${c.title} (${c.type})`, value: c.id }))}
                          value={{
                            label: (() => {
                              const t = candidates.find((c) => String(c.id) === String(d.targetId));
                              return t ? `${t.title} (${t.type})` : '';
                            })(),
                            value: d.targetId,
                          }}
                          onChange={(opt) => opt && setDecision(i, { targetId: opt.value })}
                        />
                      </div>
                    )}
                    <RadioButton
                      text="Skip this column"
                      name={`spt-map-${d.section}-${d.column.id}`}
                      checked={d.action === 'skip'}
                      onSelect={() => setDecision(i, { action: 'skip' })}
                    />
                  </div>
                  {/*
                    Map and Skip apply to this run only — the titles still differ
                    afterwards, so the dialog returns on every apply until one side
                    is renamed. Create needs no warning: it removes the mismatch.
                  */}
                  {(d.action === 'map' || d.action === 'skip') && (
                    <div className="spt-runwarn" role="alert">
                      <Warning className="spt-runwarn__icon" size="16" aria-hidden="true" />
                      <div>
                        <span className="spt-runwarn__lead">Applies to this run only.</span>
                        <span className="spt-runwarn__body">
                          Please make sure the template matches the board's columns, or this dialog will
                          reappear.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
              </React.Fragment>
            ))}
          </div>
        </ModalContent>
      </ModalBasicLayout>
      <ModalFooter
        primaryButton={{ text: 'Apply template', onClick: () => onConfirm(decisions) }}
        secondaryButton={{ text: 'Cancel', onClick: onCancel }}
      />
    </Modal>
  );
}
